import { Link, createFileRoute } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { JsonLd } from "@/components/JsonLd";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { SITE } from "@/content/site";
import { CASES } from "@/content/cases";
import { ArrowUpRight, ExternalLink } from "lucide-react";

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Credentials", path: "/credentials" },
];

const EXPERIENCE = [
  {
    t: "SEO consulting",
    d: "Service areas include technical SEO, local search, ecommerce and AI-search readiness.",
  },
  {
    t: "Business-specific planning",
    d: "Audits, retainers and one-off recoveries across Bangladesh, Australia, Switzerland, Germany, Singapore and the US — B2B SaaS, Shopify stores, clinics, studios and local service businesses.",
  },
  {
    t: "Migration and penalty recovery",
    d: "Post-migration indexation collapses, ranking drops after redesigns, and helpful-content pressure. Diagnostics run against Search Console and log data, not guesswork.",
  },
  {
    t: "AEO and GEO planning",
    d: "Structured-data and entity work aimed at ChatGPT, Perplexity, Claude, Gemini and Google AI Overviews — a service most agencies in this market still don't offer.",
  },
];

const WHAT_I_KNOW = [
  "Technical SEO and crawl/indexation diagnostics",
  "Core Web Vitals and front-end performance",
  "Local SEO and Google Business Profile",
  "Ecommerce and Shopify SEO",
  "Structured data (JSON-LD) implementation",
  "Answer Engine Optimization (AEO)",
  "Generative Engine Optimization (GEO)",
  "Bengali-language and bilingual SEO",
  "Keyword research and content strategy",
  "Search Console and GA4 measurement",
];

const PROOF = [
  {
    t: "Documented case studies",
    d: `${CASES.length} project examples and illustrative workflows with their evidence limitations stated.`,
    to: "/case-studies" as const,
  },
  {
    t: "Client testimonials",
    d: "Review verification status and public business profiles. Unsupported quotations are not published.",
    to: "/testimonials" as const,
  },
  {
    t: "Published answers and guides",
    d: "Practical guides and a public Q&A library. Unsupported research findings have been withdrawn.",
    to: "/ai-answers" as const,
  },
  {
    t: "Public working notes",
    d: "Long-form guides, checklists and free tools — the same frameworks used in paid engagements, published openly.",
    to: "/blog" as const,
  },
];

const PROFILES = [
  { t: "LinkedIn", href: SITE.social.linkedin },
  { t: "Google Business Profile", href: SITE.social.googleBusiness },
  { t: "YouTube", href: SITE.social.youtube },
  { t: "Facebook", href: SITE.social.facebook },
];

const HOW_I_WORK = [
  "Scope, deliverables and timelines are written down before any invoice.",
  "You keep admin access to your own Search Console, GA4, hosting and Google Business Profile.",
  "Reporting is built from Google's own data, not a vendor dashboard that only shows good news.",
  "No purchased links, private blog networks or ranking guarantees — the downside lands on your domain, not mine.",
  "If a tactic carries risk, you hear the risk before it ships.",
];

export const Route = createFileRoute("/credentials")({
  head: () =>
    buildSeo({
      title: "Credentials & Proof — Rahatul Ashiq Tamal (Freelancer Tamal)",
      description:
        "Freelancer Tamal’s SEO service areas, project examples, evidence limitations, practical guides and public profiles.",
      path: "/credentials",
      ogEyebrow: "Credentials",
      keywords:
        "Rahatul Ashiq Tamal credentials, SEO expert experience Bangladesh, Freelancer Tamal proof, SEO consultant background",
    }),
  component: CredentialsPage,
});

function CredentialsPage() {
  const personSchema = {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": `${SITE.url}/#person`,
    name: SITE.fullName,
    alternateName: SITE.name,
    url: `${SITE.url}/credentials`,
    jobTitle: "SEO, AEO & GEO Consultant",
    email: SITE.email,
    telephone: SITE.phone,
    knowsAbout: WHAT_I_KNOW,
    knowsLanguage: ["en", "bn"],
    address: {
      "@type": "PostalAddress",
      streetAddress: SITE.streetAddress,
      addressLocality: SITE.city,
      addressRegion: SITE.region,
      postalCode: SITE.postalCode,
      addressCountry: SITE.countryCode,
    },
    sameAs: PROFILES.map((p) => p.href),
    worksFor: {
      "@type": "Organization",
      name: SITE.name,
      url: SITE.url,
    },
  };

  return (
    <>
      <JsonLd data={personSchema} />
      <JsonLd data={breadcrumbSchema(CRUMBS)} />
      <Breadcrumbs items={CRUMBS} />

      <Section headingLevel={1}
        eyebrow="Credentials"
        title={
          <>
            The background, and the <span className="text-gradient">receipts behind it.</span>
          </>
        }
      >
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8">
          Anyone can call themselves an SEO expert. This page lists what I've actually done, what I
          know well enough to be accountable for, and where you can check it yourself. No
          certificates bought from a course platform, no invented awards.
        </p>
      </Section>

      <Section className="pt-0 pb-16">
        <h2 className="font-display text-2xl mb-6">Experience</h2>
        <div className="grid gap-6 sm:grid-cols-2">
          {EXPERIENCE.map((e) => (
            <div key={e.t} className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-display text-xl">{e.t}</h3>
              <p className="mt-3 text-sm text-muted-foreground">{e.d}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section className="pt-0 pb-16">
        <h2 className="font-display text-2xl mb-6">Areas of expertise</h2>
        <ul className="flex flex-wrap gap-2">
          {WHAT_I_KNOW.map((k) => (
            <li
              key={k}
              className="rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground"
            >
              {k}
            </li>
          ))}
        </ul>
      </Section>

      <Section className="pt-0 pb-16">
        <h2 className="font-display text-2xl mb-6">Verifiable proof</h2>
        <div className="grid gap-6 sm:grid-cols-2">
          {PROOF.map((p) => (
            <Link
              key={p.t}
              to={p.to}
              className="group rounded-2xl border border-border bg-card p-6 transition hover:border-primary/40"
            >
              <h3 className="font-display text-xl group-hover:text-primary transition">{p.t}</h3>
              <p className="mt-3 text-sm text-muted-foreground">{p.d}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm text-primary">
                Check it <ArrowUpRight className="h-4 w-4" />
              </span>
            </Link>
          ))}
        </div>
      </Section>

      <Section className="pt-0 pb-16">
        <h2 className="font-display text-2xl mb-6">Public profiles</h2>
        <div className="flex flex-wrap gap-3">
          {PROFILES.map((p) => (
            <a
              key={p.t}
              href={p.href}
              rel="me noopener"
              target="_blank"
              className="inline-flex items-center gap-2 rounded-full border border-border px-5 py-3 text-sm hover:border-primary/40"
            >
              {p.t} <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
            </a>
          ))}
        </div>
      </Section>

      <Section className="pt-0 pb-24">
        <h2 className="font-display text-2xl mb-6">How engagements run</h2>
        <ul className="max-w-3xl space-y-3">
          {HOW_I_WORK.map((h) => (
            <li key={h} className="flex gap-3 text-muted-foreground">
              <span aria-hidden="true" className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              <span>{h}</span>
            </li>
          ))}
        </ul>
        <div className="mt-10 flex flex-wrap gap-3">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-7 py-4 text-sm font-medium"
          >
            Book a scoping call <ArrowUpRight size={16} />
          </Link>
          <Link
            to="/about"
            className="inline-flex items-center gap-2 rounded-full border border-border px-7 py-4 text-sm font-medium"
          >
            Read the longer story
          </Link>
        </div>
      </Section>
    </>
  );
}
